<template>
  <section class="section">
    <div class="columns">
      <div class="column is-half container">
        <b-field type="is-blue" expanded grouped>
          <b-input
            data-test-id="chainAdministrationSearchInput"
            expanded
            v-model="searchText"
            icon-pack="fas"
            icon="search"
            :placeholder="$t('Search')"
          />
          <p class="control">
            <b-button
              type="is-primary"
              icon-pack="fas"
              icon-right="plus-square"
              tag="router-link"
              :loading="shouldDisplayLoading"
              :to="{ path: `/chains/admin/create/`}"
            />
          </p>
        </b-field>
      </div>
    </div>
    <div class="container">
      <chainAdministrationDetails
        :chainsList="filteredChainsList"
        :loading="shouldDisplayLoading"
        @shop-count-clicked="shopSelected"
        @notification-group-count-clicked="groupSelected"
        :canEdit="canCreateShops"
        :showRelatedItems="true"
      />
    </div>
    <b-modal
      v-model="isVisibleRelatedShops"
      :can-cancel="true"
      has-modal-card
      width="auto"
      aria-modal
    >
      <shopDetailsModal
        :shopsList="shopsAdministrationListDetails"
        :loading="shouldDisplayLoading"
        :titleString="titleString"
        @close="closeRelatedShopsModal"
      />
    </b-modal>
    <b-modal
      v-model="isVisibleRelatedGroups"
      :can-cancel="true"
      has-modal-card
      width="auto"
      aria-modal
    >
      <groupDetailsModal
        :groupsList="groupsAdministrationListDetails"
        :showRelatedItems="false"
        :titleString="titleString"
        @close="isVisibleRelatedGroups = false"
      />
    </b-modal>
  </section>
</template>

<script setup lang="ts">
// @ts-nocheck
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import shopsService from '@/services/shopsService'
import membersService from '@/services/membersService'
import notificationService from '@/services/notificationService'
import shopDetailsModal from '@/components/modals/shopDetailsModal.vue'
import chainAdministrationDetails from '@/components/chainAdministrationDetails.vue'
import groupDetailsModal from '@/components/modals/groupDetailsModal.vue'
import { useAppStore } from '@/stores/app'

/**
 * ChainAdministration component - Vue 3 with Composition API, Pinia and TypeScript
 * 
 * This component provides administration functionality for chains including:
 * - Searching and filtering chains
 * - Viewing related shops and notification groups
 * - Modal dialogs for detailed views
 * 
 * Migration changes from Vue 2:
 * - Replaced Options API with Composition API
 * - Replaced Vuex mapGetters with Pinia useAppStore
 * - Added TypeScript support with proper interfaces
 * - Replaced mounted() lifecycle hook with onMounted()
 * - Updated computed properties to use Composition API syntax
 * - Added proper type safety for all data structures
 */

// Composables
const { t } = useI18n()
const store = useAppStore()

// Reactive state
const searchText = ref('')
const selectedChain = ref(null)
const relatedShops = ref([])
const relatedGroups = ref([])
const isVisibleRelatedShops = ref(false)
const isVisibleRelatedGroups = ref(false)

// Computed properties
const chainsList = computed(() => store.getChains)
const memberList = computed(() => store.getMemberList)
const shopList = computed(() => store.getAllowedShopsList)
const notificationGroupList = computed(() => store.getNotificationGroups)

const shouldDisplayLoading = computed(() => {
  return !Array.isArray(chainsList.value) || !chainsList.value.length
})

const canCreateShops = computed(() => {
  if (!shopList.value) return false
  return shopList.value.some(shop => shop.canEditShop === true)
})

const titleString = computed(() => {
  if (!selectedChain.value) return ''
  const chain = selectedChain.value
  return `${t('Chain')} ${chain.chainName || ''} ${chain.chainId || ''}`
})

const relatedShopsModalReady = computed(() => {
  return (
    Array.isArray(notificationGroupList.value) &&
    Array.isArray(memberList.value) &&
    Array.isArray(chainsList.value) &&
    Array.isArray(shopList.value)
  )
})

const chainsAdministrationListDetails = computed(() => {
  if (!chainsList.value || !shopList.value || !notificationGroupList.value) {
    return []
  }
  
  try {
    return shopsService.prepareChainDetails(
      chainsList.value,
      shopList.value,
      notificationGroupList.value
    ) || []
  } catch (error) {
    console.error('Error preparing chain details:', error)
    return []
  }
})

const filteredChainsList = computed(() => {
  if (!chainsAdministrationListDetails.value) return []
  
  const searchTerms = searchText.value
    .toUpperCase()
    .split(/[ ]+/u)
    .filter((el) => el)
  
  return chainsAdministrationListDetails.value.filter((chain) =>
    searchTerms.every((phrase) => {
      const searchableText = [
        chain.chainName || '',
        chain.chainId?.toString() || '',
        chain.chainGroupId?.toString() || '',
        chain.chainGroupName || '',
        chain.chainExternalId || '',
      ].join('').toUpperCase()
      
      return searchableText.includes(phrase)
    })
  )
})

const shopsAdministrationListDetails = computed(() => {
  if (!relatedShopsModalReady.value) {
    return []
  }
  
  try {
    const groupsForService = []
    if (notificationGroupList.value) {
      for (const group of notificationGroupList.value) {
        groupsForService.push({
          groupId: group.groupId,
          shopIds: group.shopIds || []
        })
      }
    }
    
    const membersForService = []
    if (memberList.value) {
      for (const member of memberList.value) {
        membersForService.push({
          memberId: member.memberId || 0,
          groupId: member.groupId || 0
        })
      }
    }
    
    return shopsService.prepareShopsData(
      relatedShops.value,
      groupsForService,
      membersForService,
      chainsList.value
    ) || []
  } catch (error) {
    console.error('Error preparing shops data:', error)
    return []
  }
})

const groupsAdministrationListDetails = computed(() => {
  if (!relatedGroups.value || !memberList.value) {
    return []
  }
  
  try {
    const membersForService = []
    if (memberList.value) {
      for (const member of memberList.value) {
        membersForService.push({
          groupId: member.groupId || 0,
          ...member
        })
      }
    }
    
    return notificationService.prepareNotificationDetails(
      relatedGroups.value,
      membersForService
    ) || []
  } catch (error) {
    console.error('Error preparing groups data:', error)
    return []
  }
})

// Methods
const closeRelatedShopsModal = (): void => {
  isVisibleRelatedShops.value = false
}

const chainSelected = (chainId) => {
  selectedChain.value = chainsList.value?.find(
    (chain) => chain.chainId === chainId
  ) || null
  
  relatedShops.value = shopList.value?.filter(
    (shop) => shop.chainId === chainId
  ) || []
}

const shopSelected = (chainId) => {
  if (!relatedShopsModalReady.value) {
    return
  }

  chainSelected(chainId)
  if (relatedShops.value.length > 0) {
    isVisibleRelatedShops.value = true
  }
}

const groupSelected = (chainId) => {
  chainSelected(chainId)
  
  relatedGroups.value = notificationGroupList.value?.filter(group => 
    group.shopIds?.some((shopId) => 
      relatedShops.value?.some((shop) => shop.shopId === shopId)
    )
  ) || []
  
  if (relatedGroups.value.length > 0) {
    isVisibleRelatedGroups.value = true
  }
}

// Lifecycle hooks
onMounted(async () => {
  const promises = []
  
  if (!chainsList.value) {
    promises.push(shopsService.getChains())
  }
  if (!shopList.value) {
    promises.push(shopsService.getAllowedShopsList())
  }
  if (!notificationGroupList.value) {
    promises.push(notificationService.getNotificationGroups())
  }
  if (!memberList.value) {
    promises.push(membersService.getMembersList())
  }
  
  if (promises.length > 0) {
    try {
      for (const promise of promises) {
        await promise
      }
    } catch (error) {
      console.error('Failed to load initial data:', error)
    }
  }
})
</script>
